import { lazy } from 'react';
import { PermissionEn } from '@martech-solution/utils/constants';
import purchasePath from 'src/constants/urlPath/erp/purchasePath';
import type { RouteType } from 'src/hooks/useRouteElements';
import { HRTicketCategoryEn } from 'src/types/master_data/hrTicketCategory.type';
import NewLayout from '../../layouts/NewFeatures/MainLayout';

const Purchase = lazy(() => import('src/pages/Purchase'));

const PurchaseRequest = lazy(() => import('src/pages/ERP/Planning/Pr'));
const AddPurchaseRequest = lazy(() => import('src/pages/ERP/Planning/Pr/AddPurchaseRequest'));

const PQR = lazy(() => import('src/pages/Purchase/PQR'));
const PurchaseDocument = lazy(() => import('src/pages/Purchase/PurchaseDocument'));
const PurchaseOrder = lazy(() => import('src/pages/Purchase/PurchaseOrder'));
const PurchaseQuotation = lazy(() => import('src/pages/Purchase/PurchaseQuotation'));
const GRPOR = lazy(() => import('src/pages/Purchase/GRPOR'));
const AddGRPOR = lazy(() => import('src/pages/Purchase/GRPOR/AddGRPOR'));

const Qualified = lazy(() => import('src/pages/ERP/Purchase/Qualified'));
const QualifiedForm = lazy(() => import('src/pages/ERP/Purchase/Qualified/Form/QualifiedForm'));

const ReceivePlan = lazy(() => import('src/pages/Purchase/ReceivePlan'));
const HistoryOrder = lazy(() => import('src/pages/Purchase/HistoryOrder'));
const DebtGroup = lazy(() => import('src/pages/Purchase/DebtGroup'));
const Debt = lazy(() => import('src/pages/Purchase/Debt'));
const SetOfDocument = lazy(() => import('src/pages/Purchase/SetOfDocument'));
const DocumentType = lazy(() => import('src/pages/Purchase/DocumentType'));
const RefKey = lazy(() => import('src/pages/Purchase/RefKey'));

const TradePaymentMethod = lazy(() => import('src/pages/ERP/Accounting/TradePaymentMethod'));
const TradePaymentMethodForm = lazy(() => import('src/pages/ERP/Accounting/TradePaymentMethod/TradePaymentMethodForm'));
const ShippingStage = lazy(() => import('src/pages/ERP/Accounting/ShippingStage'));
const ShippingStageForm = lazy(() => import('src/pages/ERP/Accounting/ShippingStage/ShippingStageForm'));

const purchaseRoutes: RouteType[] = [
  {
    path: purchasePath.purchase,
    Layout: NewLayout,
    Component: Purchase
  },

  // PR - Yêu cầu mua hàng
  {
    path: purchasePath.pr,
    Layout: NewLayout,
    Component: PurchaseRequest,
    permission: PermissionEn.read
  },
  {
    path: `${purchasePath.prDetail}/:id`,
    Layout: NewLayout,
    Component: AddPurchaseRequest,
    checkPath: purchasePath.pr,
    permission: PermissionEn.viewDetail
  },
  {
    path: purchasePath.prAdd,
    Layout: NewLayout,
    Component: AddPurchaseRequest,
    checkPath: purchasePath.pr,
    permission: PermissionEn.create
  },
  {
    path: `${purchasePath.prEdit}/:id`,
    Layout: NewLayout,
    Component: AddPurchaseRequest,
    checkPath: purchasePath.pr,
    permission: PermissionEn.update
  },

  // Qualified - Nhà cung cấp - Đã đánh giá, đủ điều kiện hợp tác (giấy tờ, năng lực, compliance).
  {
    path: purchasePath.qualified,
    Layout: NewLayout,
    Component: Qualified,
    permission: PermissionEn.read
  },
  {
    path: purchasePath.qualifiedAdd,
    Layout: NewLayout,
    checkPath: purchasePath.qualified,
    Component: QualifiedForm,
    permission: PermissionEn.create
  },
  {
    path: `${purchasePath.qualifiedEdit}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.qualified,
    Component: QualifiedForm,
    permission: PermissionEn.update
  },
  {
    path: `${purchasePath.qualifiedDetail}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.qualified,
    Component: QualifiedForm,
    permission: PermissionEn.viewDetail
  },

  // PQR - Yêu cầu báo giá mua hàng
  {
    path: purchasePath.purchaseQuotationRequest,
    Layout: NewLayout,
    Component: PQR,
    permission: PermissionEn.read
  },
  {
    path: purchasePath.purchaseQuotationRequestAdd,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseQuotationRequest,
    Component: PurchaseDocument,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_QUOTATION_REQUEST,
    permission: PermissionEn.create
  },
  {
    path: `${purchasePath.purchaseQuotationRequestEdit}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseQuotationRequest,
    Component: PurchaseDocument,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_QUOTATION_REQUEST,
    permission: PermissionEn.update
  },
  {
    path: `${purchasePath.purchaseQuotationRequestDetail}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseQuotationRequest,
    Component: PurchaseDocument,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_QUOTATION_REQUEST,
    permission: PermissionEn.viewDetail
  },

  // PQ - Báo giá mua hàng
  {
    path: purchasePath.purchaseQuotation,
    Layout: NewLayout,
    Component: PurchaseQuotation,
    permission: PermissionEn.read
  },
  {
    path: purchasePath.purchaseQuotationAdd,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseQuotation,
    Component: PurchaseDocument,
    permission: PermissionEn.create,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_QUOTATION
  },
  {
    path: `${purchasePath.purchaseQuotationEdit}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseQuotation,
    Component: PurchaseDocument,
    permission: PermissionEn.update,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_QUOTATION
  },
  {
    path: `${purchasePath.purchaseQuotationDetail}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseQuotation,
    Component: PurchaseDocument,
    permission: PermissionEn.viewDetail,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_QUOTATION
  },

  // PO - Đơn đặt mua hàng
  {
    path: purchasePath.purchaseOrder,
    Layout: NewLayout,
    Component: PurchaseOrder,
    permission: PermissionEn.read
  },
  {
    path: purchasePath.purchaseOrderAdd,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseOrder,
    Component: PurchaseDocument,
    permission: PermissionEn.create,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_ORDER
  },
  {
    path: `${purchasePath.purchaseOrderEdit}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseOrder,
    Component: PurchaseDocument,
    permission: PermissionEn.update,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_ORDER
  },
  {
    path: `${purchasePath.purchaseOrderDetail}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.purchaseOrder,
    Component: PurchaseDocument,
    permission: PermissionEn.viewDetail,
    ticketCategoryCode: HRTicketCategoryEn.PURCHASE_ORDER
  },

  // GRPOR - Yêu cầu nhập kho
  {
    path: purchasePath.grpor,
    Layout: NewLayout,
    Component: GRPOR,
    permission: PermissionEn.read
  },
  {
    path: purchasePath.grporAdd,
    Layout: NewLayout,
    checkPath: purchasePath.grpor,
    Component: AddGRPOR,
    permission: PermissionEn.create,
    ticketCategoryCode: HRTicketCategoryEn.GOOD_RECEIPT_PO_REQUEST
  },
  {
    path: `${purchasePath.grporEdit}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.grpor,
    Component: AddGRPOR,
    permission: PermissionEn.update,
    ticketCategoryCode: HRTicketCategoryEn.GOOD_RECEIPT_PO_REQUEST
  },
  {
    path: `${purchasePath.grporDetail}/:id`,
    Layout: NewLayout,
    checkPath: purchasePath.grpor,
    Component: AddGRPOR,
    permission: PermissionEn.viewDetail,
    ticketCategoryCode: HRTicketCategoryEn.GOOD_RECEIPT_PO_REQUEST
  },

  // Payment Method
  {
    path: purchasePath.tradePaymentMethod,
    Layout: NewLayout,
    Component: TradePaymentMethod
  },
  {
    path: purchasePath.tradePaymentMethodAdd,
    Layout: NewLayout,
    Component: TradePaymentMethodForm
  },
  {
    path: `${purchasePath.tradePaymentMethodEdit}/:id`,
    Layout: NewLayout,
    Component: TradePaymentMethodForm
  },
  {
    path: `${purchasePath.tradePaymentMethodDetail}/:id`,
    Layout: NewLayout,
    Component: TradePaymentMethodForm
  },
  // Shipping stage
  {
    path: purchasePath.shippingStage,
    Layout: NewLayout,
    Component: ShippingStage
  },
  {
    path: purchasePath.shippingStageAdd,
    Layout: NewLayout,
    Component: ShippingStageForm
  },
  {
    path: `${purchasePath.shippingStageEdit}/:id`,
    Layout: NewLayout,
    Component: ShippingStageForm
  },
  {
    path: `${purchasePath.shippingStageDetail}/:id`,
    Layout: NewLayout,
    Component: ShippingStageForm
  },

  // Receive plan
  {
    path: purchasePath.receivePlan,
    Layout: NewLayout,
    Component: ReceivePlan,
    permission: PermissionEn.read
  },
  // Order history
  {
    path: purchasePath.orderHistory,
    Layout: NewLayout,
    Component: HistoryOrder,
    permission: PermissionEn.read
  },
  // Debt group
  {
    path: purchasePath.debtGroup,
    Layout: NewLayout,
    Component: DebtGroup,
    permission: PermissionEn.read
  },
  // Debt
  {
    path: purchasePath.debt,
    Layout: NewLayout,
    Component: Debt,
    permission: PermissionEn.read
  },
  // SetOfDocument - Bộ chứng từ
  {
    path: purchasePath.setOfDocument,
    Layout: NewLayout,
    Component: SetOfDocument,
    permission: PermissionEn.read
  },
  // DocumentType - Loại chứng từ
  {
    path: purchasePath.documentType,
    Layout: NewLayout,
    Component: DocumentType,
    permission: PermissionEn.read
  },
  // RefKey
  {
    path: purchasePath.refKey,
    Layout: NewLayout,
    Component: RefKey,
    permission: PermissionEn.read
  }
];

export default purchaseRoutes;
